import { Button, Card, Descriptions, Message, Modal, Space, Table, Tag, DatePicker, Upload } from "@arco-design/web-react";
import { EnumLabel } from "@/components/Enum";
import { StatusTag } from "@/components/StatusTag";
import { EditableTable } from "@/components/EditableTable";
import { StatusDef, Status } from "@/enums/status";
import { TermsDef } from "@/enums/common";
import { VendorAPI } from "@/apis/purchasing";
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IconEdit, IconCheck, IconClose, IconLeft, IconBook, IconUpload } from "@arco-design/web-react/icon";
import { baseUrl } from "@/utils/api";
import Address from "@/components/Address";

const docColumns = [
    { title: "Name", dataIndex: "document.docName" },
    {
        title: "Expiration Date", dataIndex: "expiredDate",
        editableRender: (col, record) => (
            <DatePicker style={{ width: 200 }} />
        )
    },
];

export const VendorInfo = () => {
    const params = useParams();
    const vendorId = params.vendorId;
    const navigate = useNavigate();
    const [vendor, setVendor] = useState({});
    const [statusLoading, setStatusLoading] = useState(false);
    const [docVisible, setDocVisible] = useState(false);
    const [docData, setDocData] = useState([]);
    const [docConfirmLoading, setDocConfirmLoading] = useState(false);

    const getVendor = useCallback(async () => {
        const info = await VendorAPI.get(vendorId);
        setVendor(info);
    }, [vendorId]);

    useEffect(() => {
        getVendor();
    }, [getVendor]);

    const updateStatus = async (status) => {
        setStatusLoading(true);
        try {
            if (status === Status.Active) {
                await VendorAPI.enable(vendorId);
            } else {
                await VendorAPI.disable(vendorId);
            }
            Message.success("Status updated successfully");
            await getVendor();
        } catch (error) {
            Message.error(error.message);
        } finally {
            setStatusLoading(false);
        }
    };

    const getDocs = async () => {
        let docs = await VendorAPI.getDocuments(vendorId);
        setDocData(docs);
    };

    const afterDocUpload = (files, file) => {
        switch (file.status) {
            case "error":
                setDocConfirmLoading(false);
                Message.error("Document upload failed");
                break;
            case "done":
                setDocConfirmLoading(false);
                getDocs();
                Message.success("Document upload successfully");
                break;
            default:
                break;
        }
    };

    const updateDoc = async (row) => {
        await VendorAPI.updateDocument(row.id, row);
        Message.success("Document updated successfully");
        getDocs();
    };

    const deleteDoc = async (id) => {
        await VendorAPI.deleteDocument(id);
        Message.success("Document deleted successfully");
        setDocData(docData.filter(item => item.id !== id));
    };

    return (
        <>
            <Card title={
                <Space>
                    <span>{vendor.vendorName}</span>
                    <Tag><EnumLabel enumObj={StatusDef} value={vendor.status} /></Tag>
                </Space>
            } extra={
                <Space>
                    {vendor.status === Status.Active &&
                        <Button status="danger" icon={<IconClose />} onClick={() => updateStatus(Status.Inactive)} loading={statusLoading}>Disable</Button>
                    }
                    {vendor.status === Status.Inactive &&
                        <Button status="success" icon={<IconCheck />} onClick={() => updateStatus(Status.Active)} loading={statusLoading}>Enable</Button>
                    }
                    <Button type="primary" icon={<IconEdit />} onClick={() => navigate(`/workspace/purchasing/vendor/${vendorId}/edit`)}>Edit</Button>
                    <Button icon={<IconBook />} onClick={() => {
                        getDocs();
                        setDocVisible(true);
                    }}>Document</Button>
                    <Button icon={<IconLeft />} onClick={() => navigate("/workspace/purchasing/vendor")}>Back</Button>
                </Space>
            }>
                <Descriptions colon=":"
                    column={2}
                    data={[
                        { label: "Vendor Name", value: vendor.vendorName },
                        { label: "Term", value: <EnumLabel enumObj={TermsDef} value={vendor.term} /> },
                    ]} />
                <Descriptions column={1} layout="vertical" colon=":"
                    labelStyle={{ paddingRight: 36 }}
                    data={[
                        {
                            label: "Main Contact",
                            value: <Table rowKey="id" columns={[
                                { title: "First Name", dataIndex: "firstName" },
                                { title: "Last Name", dataIndex: "lastName" },
                                { title: "Tags", render: (col, record) => <StatusTag.List value={record.tags} /> },
                                { title: "Email", dataIndex: "email" },
                                { title: "Phone", dataIndex: "phone" },
                            ]} data={vendor.contacts} />
                        }
                    ]} />
                <Descriptions column={1} colon=":"
                    labelStyle={{ paddingRight: 36 }}
                    data={[
                        {
                            label: "Billing",
                            value: <Address value={{
                                address: vendor.billingAddress,
                                city: vendor.billingCity,
                                state: vendor.billingState,
                                zip: vendor.billingZip
                            }} inline />
                        }
                    ]} />
                <Descriptions column={1} layout="vertical" colon=":"
                    labelStyle={{ paddingRight: 36 }}
                    data={[
                        {
                            label: "Shipping",
                            value: <Table rowKey="id" columns={[
                                { title: "Ship to Name", dataIndex: "contactName" },
                                { title: "Address", dataIndex: "address" },
                                { title: "City", dataIndex: "city" },
                                { title: "State", dataIndex: "state" },
                                { title: "Zip", dataIndex: "zip" },
                            ]} data={vendor.shipAddresses} />
                        }
                    ]} />
            </Card>
            <Modal title="Vendor Documents" visible={docVisible}
                style={{ width: 700 }}
                onCancel={() => setDocVisible(false)}
                footer={
                    <Space>
                        <Button onClick={() => setDocVisible(false)}>Close</Button>
                        <Upload withCredentials={true} showUploadList={null}
                            headers={{ 'bee-access-token': localStorage.getItem("login.token") }}
                            action={`${baseUrl}/vendor/document?vendorId=${vendorId}`}
                            beforeUpload={() => setDocConfirmLoading(true)}
                            onChange={afterDocUpload}>
                            <Button type="primary" icon={<IconUpload />}
                                loading={docConfirmLoading}>Upload Document</Button>
                        </Upload>
                    </Space>
                }
            >
                <EditableTable columns={docColumns} data={docData} rowKey="id"
                    onRowDelete={row => deleteDoc(row.id)}
                    onRowSave={updateDoc}></EditableTable>
            </Modal>
        </>
    );
};
